import type { Vec3 } from '../../geo/types';
import type { GableType } from './types';
import { HOUSE, computeSteppedGableSteps } from './houseStyle';

/**
 * Front-gable outlines in the facade plane x = 0 of the house frame, as (0, y, z) points
 * running counter-clockwise seen from the street: bottom-left, bottom-right, then up the
 * right side, over the top and down to the left eave corner.
 */
export interface GableOutline {
  readonly points: readonly Vec3[];
  readonly ridgeY: number;
  readonly apex: Vec3;
  readonly coping: readonly Vec3[];
}

function outline(width: number, eavesHeight: number, top: readonly Vec3[]): GableOutline {
  const half = width / 2;
  const points: Vec3[] = [
    [0, 0, -half],
    [0, 0, half],
    [0, eavesHeight, half],
    ...top,
    [0, eavesHeight, -half],
  ];
  const apex = top.reduce((best, p) => (p[1] > best[1] || (p[1] === best[1] && p[2] === 0) ? p : best));
  return { points, ridgeY: apex[1], apex, coping: points.slice(2) };
}

function mirror(side: readonly Vec3[]): Vec3[] {
  return [...side].reverse().map((p): Vec3 => [0, p[1], -p[2]]);
}

export function plainGableOutline(width: number, eavesHeight: number, pitch: number): GableOutline {
  const ridgeY = eavesHeight + (width / 2) * Math.tan(pitch);
  return outline(width, eavesHeight, [[0, ridgeY, 0]]);
}

/** Crow steps: risers at the step ends, treads `overhang` above the roof line at their inner end. */
export function steppedGableOutline(
  width: number,
  eavesHeight: number,
  pitch: number
): GableOutline {
  const half = width / 2;
  const rise = half * Math.tan(pitch);
  const steps = computeSteppedGableSteps(rise);
  const stepRise = rise / steps;
  const stepRun = half / steps;
  const lift = HOUSE.steppedGable.overhang;
  const right: Vec3[] = [];
  for (let i = 0; i < steps; i++) {
    const tread = eavesHeight + (i + 1) * stepRise + lift;
    const outer = half - i * stepRun;
    right.push([0, tread, outer]);
    if (i < steps - 1) right.push([0, tread, outer - stepRun]);
  }
  const crest = right[right.length - 1]![1];
  return outline(width, eavesHeight, [...right, [0, crest, 0], ...mirror(right)]);
}

export function spoutGableOutline(width: number, eavesHeight: number, pitch: number): GableOutline {
  const half = width / 2;
  const rise = half * Math.tan(pitch);
  const neck = (width * HOUSE.spoutGable.neckWidthRatio) / 2;
  const shoulder = eavesHeight + rise * HOUSE.spoutGable.shoulderRatio;
  const neckTop = eavesHeight + rise * HOUSE.spoutGable.neckTopRatio;
  const right: Vec3[] = [
    [0, shoulder, half],
    [0, shoulder, neck],
    [0, neckTop, neck],
  ];
  return outline(width, eavesHeight, [...right, [0, eavesHeight + rise, 0], ...mirror(right)]);
}

export function gableOutline(
  type: GableType,
  width: number,
  eavesHeight: number,
  pitch: number
): GableOutline {
  switch (type) {
    case 'plain':
      return plainGableOutline(width, eavesHeight, pitch);
    case 'stepped':
      return steppedGableOutline(width, eavesHeight, pitch);
    case 'spout':
      return spoutGableOutline(width, eavesHeight, pitch);
  }
}

export function roofSlopeLength(width: number, pitch: number): number {
  return width / 2 / Math.cos(pitch);
}

function segmentNormal(a: Vec3, b: Vec3): readonly [number, number] {
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const len = Math.hypot(dy, dz);
  return [-dz / len, dy / len];
}

/**
 * Offsets an open path in the x = 0 plane along its right-hand normal (outward for the
 * counter-clockwise gable outline), mitring interior corners.
 */
export function offsetPath(path: readonly Vec3[], distance: number): Vec3[] {
  return path.map((p, i): Vec3 => {
    const before = i > 0 ? segmentNormal(path[i - 1]!, p) : undefined;
    const after = i < path.length - 1 ? segmentNormal(p, path[i + 1]!) : undefined;
    const n1 = before ?? after!;
    const n2 = after ?? before!;
    const my = n1[0] + n2[0];
    const mz = n1[1] + n2[1];
    const len = Math.hypot(my, mz);
    const scale = distance / (((my / len) * n2[0] + (mz / len) * n2[1]) * len);
    return [p[0], p[1] + my * scale, p[2] + mz * scale];
  });
}
